import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import { profileAPI, cartAPI, purchasesAPI, ordersAPI } from '../lib/api';
import CabinetSubscriptions from '../components/cabinet/CabinetSubscriptions';
import { Trash2, ShoppingCart, Package, Building2, ArrowLeft, ArrowRight, Check, Settings2 } from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import PersonalDataConsent from '../components/PersonalDataConsent';
import MarketingConsent from '../components/MarketingConsent';
import { Link } from 'react-router-dom';
import { LEGAL_LINKS } from '../data/legalLinks';

const DashboardPage = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(tabFromUrl || 'profile');
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [profileForm, setProfileForm] = useState({
    inn: '',
    organization_name: '',
    legal_address: '',
    correspondence_address: '',
    contact_phone: '',
    email: '',
    correspondent_account: '',
    settlement_account: '',
    bik: '',
    bank_name: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState(false);

  // Cart state
  const [cart, setCart] = useState([]);
  const [cartLoading, setCartLoading] = useState(false);
  const [selectedCartItems, setSelectedCartItems] = useState(new Set());
  const [checkoutStep, setCheckoutStep] = useState(1);
  const [checkoutForm, setCheckoutForm] = useState({
    contactName: '',
    email: '',
    inn: '',
    organization_name: '',
    legal_address: '',
    correspondence_address: '',
    contact_phone: '',
    correspondent_account: '',
    settlement_account: '',
    bik: '',
    bank_name: ''
  });
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutConsent, setCheckoutConsent] = useState(false);
  const [checkoutMarketingConsent, setCheckoutMarketingConsent] = useState(false);
  const [orderNumber, setOrderNumber] = useState(null);

  // Purchases state
  const [purchases, setPurchases] = useState([]);
  const [purchasesLoading, setPurchasesLoading] = useState(false);

  // Обновляем активную вкладку при изменении параметра tab в URL
  useEffect(() => {
    const tabFromUrl = searchParams.get('tab');
    if (tabFromUrl && ['profile', 'cart', 'purchases', 'management'].includes(tabFromUrl)) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Обновляем URL при изменении активной вкладки
  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams({ tab: value });
  };

  const loadProfile = useCallback(async () => {
    try {
      setProfileLoading(true);
      setProfileError('');
      const data = await profileAPI.getProfile();
      
      if (!data) {
        // Профиль не найден, создаем пустой
        const emptyProfile = {
          id: '',
          user_id: user?.id || '',
          inn: '',
          organization_name: '',
          legal_address: '',
          correspondence_address: '',
          contact_phone: '',
          email: '',
          correspondent_account: '',
          settlement_account: '',
          bik: '',
          bank_name: ''
        };
        setProfile(emptyProfile);
        setProfileForm({
          inn: '',
          organization_name: '',
          legal_address: '',
          correspondence_address: '',
          contact_phone: '',
          email: '',
          correspondent_account: '',
          settlement_account: '',
          bik: '',
          bank_name: ''
        });
        setProfileLoading(false);
        return;
      }
      
      setProfile(data);
      setProfileForm({
        inn: data.inn || '',
        organization_name: data.organization_name || '',
        legal_address: data.legal_address || '',
        correspondence_address: data.correspondence_address || '',
        contact_phone: data.contact_phone || '',
        email: data.email || '',
        correspondent_account: data.correspondent_account || '',
        settlement_account: data.settlement_account || '',
        bik: data.bik || '',
        bank_name: data.bank_name || ''
      });
      // Заполняем форму оформления заказа данными из профиля
      setCheckoutForm({
        contactName: '',
        email: data.email || user?.email || '',
        inn: data.inn || '',
        organization_name: data.organization_name || '',
        legal_address: data.legal_address || '',
        correspondence_address: data.correspondence_address || '',
        contact_phone: data.contact_phone || '',
        correspondent_account: data.correspondent_account || '',
        settlement_account: data.settlement_account || '',
        bik: data.bik || '',
        bank_name: data.bank_name || ''
      });
      setProfileLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      setProfileError(error.message || 'Ошибка при загрузке профиля');
      setProfileLoading(false);
      // Если профиль не найден, создаем пустой профиль
      if (error.message && error.message.includes('404')) {
        setProfile({
          id: '',
          user_id: user?.id || '',
          inn: '',
          organization_name: '',
          legal_address: '',
          correspondence_address: '',
          contact_phone: '',
          email: '',
          correspondent_account: '',
          settlement_account: '',
          bik: '',
          bank_name: ''
        });
      }
    }
  }, [user]);

  const loadCart = useCallback(async () => {
    setCartLoading(true);
    try {
      const data = await cartAPI.getCart();
      setCart(data);
    } catch (error) {
      console.error('Error loading cart:', error);
    } finally {
      setCartLoading(false);
    }
  }, []);

  const loadPurchases = useCallback(async () => {
    setPurchasesLoading(true);
    try {
      const data = await purchasesAPI.getPurchases();
      setPurchases(data);
    } catch (error) {
      console.error('Error loading purchases:', error);
    } finally {
      setPurchasesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    loadProfile();
    loadCart();
    loadPurchases();
  }, [isAuthenticated, navigate, loadProfile, loadCart, loadPurchases]);

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setProfileError('');
    setProfileSuccess(false);
    setProfileLoading(true);

    try {
      const updated = await profileAPI.updateProfile(profileForm);
      setProfile(updated);
      // Обновляем форму с сохраненными данными
      setProfileForm({
        inn: updated.inn || '',
        organization_name: updated.organization_name || '',
        legal_address: updated.legal_address || '',
        correspondence_address: updated.correspondence_address || '',
        contact_phone: updated.contact_phone || '',
        email: updated.email || '',
        correspondent_account: updated.correspondent_account || '',
        settlement_account: updated.settlement_account || '',
        bik: updated.bik || '',
        bank_name: updated.bank_name || ''
      });
      setProfileSuccess(true);
      setTimeout(() => setProfileSuccess(false), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      // Если ошибка 401, перенаправляем на страницу входа
      if (error.response?.status === 401 || error.message?.includes('User not found')) {
        setProfileError('Сессия истекла. Пожалуйста, войдите снова.');
        setTimeout(() => {
          logout();
          navigate('/login');
        }, 2000);
      } else {
        setProfileError(error.message || 'Ошибка при сохранении профиля');
      }
    } finally {
      setProfileLoading(false);
    }
  };

  const handleRemoveFromCart = async (itemId) => {
    try {
      await cartAPI.removeFromCart(itemId);
      setSelectedCartItems(prev => {
        const newSet = new Set(prev);
        newSet.delete(itemId);
        return newSet;
      });
      loadCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
    }
  };

  const handleToggleCartItem = (itemId, checked) => {
    setSelectedCartItems(prev => {
      const newSet = new Set(prev);
      const nextChecked = checked === undefined ? !newSet.has(itemId) : checked;
      if (!nextChecked) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleCheckout = () => {
    if (selectedCartItems.size === 0) {
      alert('Выберите хотя бы один товар для оформления заказа');
      return;
    }
    setCheckoutStep(2);
  };

  const handleCheckoutSubmit = async (e) => {
    e.preventDefault();
    if (!checkoutConsent) {
      alert('Необходимо согласие на обработку персональных данных');
      return;
    }
    setCheckoutLoading(true);
    try {
      const orderData = {
        cart_item_ids: Array.from(selectedCartItems),
        contact_name: checkoutForm.contactName,
        email: checkoutForm.email,
        inn: checkoutForm.inn || null,
        organization_name: checkoutForm.organization_name || null,
        legal_address: checkoutForm.legal_address || null,
        correspondence_address: checkoutForm.correspondence_address || null,
        contact_phone: checkoutForm.contact_phone || null,
        correspondent_account: checkoutForm.correspondent_account || null,
        settlement_account: checkoutForm.settlement_account || null,
        bik: checkoutForm.bik || null,
        bank_name: checkoutForm.bank_name || null
      };
      
      const order = await ordersAPI.createOrder(orderData, checkoutConsent, checkoutMarketingConsent);
      setOrderNumber(order.order_number);
      setCheckoutStep(3);
      
      // Очищаем выбранные товары
      setSelectedCartItems(new Set());
      // Обновляем корзину и покупки (дожидаемся обновления)
      await loadCart();
      await loadPurchases();
    } catch (error) {
      console.error('Error submitting checkout:', error);
      alert(error.message || 'Ошибка при оформлении заказа');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (price) => {
    if (!price || price === 0) return 'по запросу';
    return new Intl.NumberFormat('ru-RU', {
      style: 'currency',
      currency: 'RUB',
      minimumFractionDigits: 0
    }).format(price);
  };

  const hasValidPrice = (item) => {
    if (item.calculator_data?.totalPrice && item.calculator_data.totalPrice > 0) return true;
    if (item.calculator_data?.monthlyPrice && item.calculator_data.monthlyPrice > 0) return true;
    if (item.calculator_data?.oneTimePrice && item.calculator_data.oneTimePrice > 0) return true;
    if (item.product?.price && item.product.price !== 'по запросу' && item.product.price !== '0') return true;
    return false;
  };

  const getItemPriceLabel = (item) => {
    if (hasValidPrice(item)) return null;
    return '- под заказ';
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead title="Личный кабинет | Антология" noindex />
      <Header />
      <div className="flex-1 container mx-auto px-4 py-8 pt-24">
        <div className="mb-6">
          <h1 className="text-3xl font-bold">Личный кабинет</h1>
          <p className="text-muted-foreground mt-2">
            Добро пожаловать, {profile?.organization_name || profileForm?.organization_name || user?.email}
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto">
            <TabsTrigger value="profile">
              <Building2 className="w-4 h-4 mr-2" />
              Профиль компании
            </TabsTrigger>
            <TabsTrigger value="cart">
              <ShoppingCart className="w-4 h-4 mr-2" />
              Корзина ({cart.length})
            </TabsTrigger>
            <TabsTrigger value="purchases">
              <Package className="w-4 h-4 mr-2" />
              Покупки ({purchases.length})
            </TabsTrigger>
            <TabsTrigger value="management">
              <Settings2 className="w-4 h-4 mr-2" />
              Управление
            </TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Профиль компании</CardTitle>
                <CardDescription>
                  Заполните информацию о вашей компании
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProfileSubmit} className="space-y-4">
                  {profileError && (
                    <Alert variant="destructive">
                      <AlertDescription>{profileError}</AlertDescription>
                    </Alert>
                  )}
                  {profileSuccess && (
                    <Alert>
                      <AlertDescription>Профиль успешно сохранен</AlertDescription>
                    </Alert>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="inn">ИНН</Label>
                      <Input
                        id="inn"
                        value={profileForm.inn}
                        onChange={(e) => setProfileForm({ ...profileForm, inn: e.target.value })}
                        placeholder="1234567890"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="organization_name">Название организации</Label>
                      <Input
                        id="organization_name"
                        value={profileForm.organization_name}
                        onChange={(e) => setProfileForm({ ...profileForm, organization_name: e.target.value })}
                        placeholder="ООО «Название компании»"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="legal_address">Юридический адрес</Label>
                      <Textarea
                        id="legal_address"
                        value={profileForm.legal_address}
                        onChange={(e) => setProfileForm({ ...profileForm, legal_address: e.target.value })}
                        placeholder="Город, улица, дом, офис"
                      />
                    </div>

                    <div className="space-y-2 md:col-span-2">
                      <Label htmlFor="correspondence_address">Адрес для корреспонденции</Label>
                      <Textarea
                        id="correspondence_address"
                        value={profileForm.correspondence_address}
                        onChange={(e) => setProfileForm({ ...profileForm, correspondence_address: e.target.value })}
                        placeholder="Город, улица, дом, офис"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact_phone">Контактный телефон</Label>
                      <Input
                        id="contact_phone"
                        value={profileForm.contact_phone}
                        onChange={(e) => setProfileForm({ ...profileForm, contact_phone: e.target.value })}
                        placeholder="+7 (999) 123-45-67"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Электронная почта</Label>
                      <Input
                        id="email"
                        type="email"
                        value={profileForm.email}
                        onChange={(e) => setProfileForm({ ...profileForm, email: e.target.value })}
                        placeholder="company@example.com"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="correspondent_account">Корреспондентский счет</Label>
                      <Input
                        id="correspondent_account"
                        value={profileForm.correspondent_account}
                        onChange={(e) => setProfileForm({ ...profileForm, correspondent_account: e.target.value })}
                        placeholder="30101810100000000593"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="settlement_account">Расчетный счет</Label>
                      <Input
                        id="settlement_account"
                        value={profileForm.settlement_account}
                        onChange={(e) => setProfileForm({ ...profileForm, settlement_account: e.target.value })}
                        placeholder="40702810100000000000"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bik">БИК</Label>
                      <Input
                        id="bik"
                        value={profileForm.bik}
                        onChange={(e) => setProfileForm({ ...profileForm, bik: e.target.value })}
                        placeholder="044525225"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="bank_name">Название банка</Label>
                      <Input
                        id="bank_name"
                        value={profileForm.bank_name}
                        onChange={(e) => setProfileForm({ ...profileForm, bank_name: e.target.value })}
                        placeholder="ПАО «Банк»"
                      />
                    </div>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Сохраняя профиль, вы подтверждаете ознакомление с{' '}
                    <Link to={LEGAL_LINKS.privacy} className="text-accent hover:underline">
                      политикой обработки персональных данных
                    </Link>
                    .
                  </p>

                  <Button type="submit" disabled={profileLoading}>
                    {profileLoading ? 'Сохранение...' : 'Сохранить'}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="cart" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Корзина</CardTitle>
                <CardDescription>
                  {checkoutStep === 1 ? 'Выбранные услуги' : checkoutStep === 2 ? 'Оформление заказа' : 'Заказ оформлен'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {checkoutStep === 1 ? (
                  <>
                    {cartLoading ? (
                      <div className="text-center py-8">Загрузка...</div>
                    ) : cart.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        Корзина пуста
                      </div>
                    ) : (
                      <>
                        <div className="space-y-4">
                          {cart.map((item) => (
                            <Card
                              key={item.id}
                              className={`${selectedCartItems.has(item.id) ? 'border-accent' : ''} cursor-pointer transition-colors`}
                              onClick={() => handleToggleCartItem(item.id)}
                            >
                              <CardContent className="pt-6">
                                <div className="flex items-start gap-4">
                                  <div className="pt-1">
                                    <Checkbox
                                      checked={selectedCartItems.has(item.id)}
                                      onCheckedChange={(checked) => handleToggleCartItem(item.id, checked)}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </div>
                                  <div className="flex-1 pointer-events-none">
                                    <h3 className="font-semibold text-lg">{item.product?.name}</h3>
                                    <p className="text-sm text-muted-foreground mt-1">
                                      {item.product?.short_description}
                                    </p>
                                    
                                    {/* Дата добавления в корзину */}
                                    <p className="text-xs text-muted-foreground mt-2">
                                      Дата оформления: {formatDate(item.created_at)}
                                    </p>
                                    
                                    {/* Данные калькулятора */}
                                    {item.calculator_data && (
                                      <div className="mt-4 space-y-2 border-t border-border/50 pt-4">
                                        {item.calculator_data.selectedServices && 
                                         item.calculator_data.selectedServices.length > 0 && (
                                          <div className="mt-2">
                                            <p className="text-sm font-medium mb-1">
                                              Дополнительные услуги и продукты:
                                            </p>
                                            <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
                                              {item.calculator_data.selectedServices.map((service, idx) => (
                                                <li key={idx}>
                                                  {service.name}
                                                  <span className="ml-2 font-medium text-foreground">
                                                    ({service.price
                                                      ? formatPrice(service.price)
                                                      : (service.priceDisplay || 'по запросу')})
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                        
                                        <div className="mt-3 pt-2 border-t border-border/30 space-y-1">
                                          {item.calculator_data.monthlyPrice > 0 && (
                                            <p className="text-sm">
                                              <span className="font-medium">Стоимость в месяц:</span>{' '}
                                              <span className="text-accent font-semibold">
                                                {formatPrice(item.calculator_data.monthlyPrice)}
                                              </span>
                                            </p>
                                          )}
                                          {item.calculator_data.oneTimePrice > 0 && (
                                            <p className="text-sm">
                                              <span className="font-medium">Разовая стоимость:</span>{' '}
                                              <span className="text-accent font-semibold">
                                                {formatPrice(item.calculator_data.oneTimePrice)}
                                              </span>
                                            </p>
                                          )}
                                           {item.calculator_data.totalPrice > 0 ? (
                                            <p className="text-sm font-semibold mt-2">
                                              <span className="font-medium">Итого:</span>{' '}
                                              <span className="text-accent text-base">
                                                {formatPrice(item.calculator_data.totalPrice)}
                                              </span>
                                              {!hasValidPrice(item) && (
                                                <span className="ml-2 text-xs text-muted-foreground">(без расчёта)</span>
                                              )}
                                            </p>
                                          ) : !hasValidPrice(item) && (
                                            <p className="text-sm font-semibold mt-2 text-muted-foreground">
                                              {getItemPriceLabel(item)}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {!item.calculator_data && item.product?.price && (
                                      <p className="text-sm font-medium mt-2">
                                        {item.product.price === '0' || item.product.price === 'по запросу' 
                                          ? getItemPriceLabel(item) 
                                          : item.product.price}
                                      </p>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => handleRemoveFromCart(item.id)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                        <div className="mt-6 flex justify-end">
                          <Button
                            onClick={handleCheckout}
                            disabled={selectedCartItems.size === 0}
                            className="bg-accent hover:bg-accent/90"
                          >
                            Оформить заказ ({selectedCartItems.size})
                            <ArrowRight className="w-4 h-4 ml-2" />
                          </Button>
                        </div>
                      </>
                    )}
                  </>
                ) : checkoutStep === 2 ? (
                  <form onSubmit={handleCheckoutSubmit} className="space-y-6">
                    {/* Навигация по шагам */}
                    <div className="flex items-center justify-between mb-6 pb-4 border-b">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setCheckoutStep(1)}
                        className="gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Назад
                      </Button>
                      <div className="text-sm text-muted-foreground">
                        Шаг 2 из 3
                      </div>
                    </div>

                    {/* ФИО контактного лица */}
                    <div className="space-y-2">
                      <Label htmlFor="contactName">ФИО контактного лица *</Label>
                      <Input
                        id="contactName"
                        value={checkoutForm.contactName}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, contactName: e.target.value })}
                        placeholder="Иванов Иван Иванович"
                        required
                      />
                    </div>

                    {/* Email для отправки заказа */}
                    <div className="space-y-2">
                      <Label htmlFor="checkoutEmail">Email для отправки заказа с ключами и инструкцией доступа *</Label>
                      <Input
                        id="checkoutEmail"
                        type="email"
                        value={checkoutForm.email}
                        onChange={(e) => setCheckoutForm({ ...checkoutForm, email: e.target.value })}
                        placeholder="order@example.com"
                        required
                      />
                    </div>

                    {/* Данные компании */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="checkoutInn">ИНН</Label>
                        <Input
                          id="checkoutInn"
                          value={checkoutForm.inn}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, inn: e.target.value })}
                          placeholder="1234567890"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="checkoutOrgName">Название организации</Label>
                        <Input
                          id="checkoutOrgName"
                          value={checkoutForm.organization_name}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, organization_name: e.target.value })}
                          placeholder="ООО «Название компании»"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="checkoutLegalAddress">Юридический адрес</Label>
                        <Textarea
                          id="checkoutLegalAddress"
                          value={checkoutForm.legal_address}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, legal_address: e.target.value })}
                          placeholder="Город, улица, дом, офис"
                        />
                      </div>

                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="checkoutCorrespondenceAddress">Адрес для корреспонденции</Label>
                        <Textarea
                          id="checkoutCorrespondenceAddress"
                          value={checkoutForm.correspondence_address}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, correspondence_address: e.target.value })}
                          placeholder="Город, улица, дом, офис"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="checkoutContactPhone">Контактный телефон</Label>
                        <Input
                          id="checkoutContactPhone"
                          value={checkoutForm.contact_phone}
                          onChange={(e) => setCheckoutForm({ ...checkoutForm, contact_phone: e.target.value })}
                          placeholder="+7 (999) 123-45-67"
                        />
                      </div>
                    </div>

                    {/* Реквизиты банка */}
                    <div className="border-t pt-4">
                      <h3 className="font-semibold mb-4">Реквизиты банка</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="checkoutCorrAccount">Корреспондентский счет</Label>
                          <Input
                            id="checkoutCorrAccount"
                            value={checkoutForm.correspondent_account}
                            onChange={(e) => setCheckoutForm({ ...checkoutForm, correspondent_account: e.target.value })}
                            placeholder="30101810100000000593"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="checkoutSettlementAccount">Расчетный счет</Label>
                          <Input
                            id="checkoutSettlementAccount"
                            value={checkoutForm.settlement_account}
                            onChange={(e) => setCheckoutForm({ ...checkoutForm, settlement_account: e.target.value })}
                            placeholder="40702810100000000000"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="checkoutBik">БИК</Label>
                          <Input
                            id="checkoutBik"
                            value={checkoutForm.bik}
                            onChange={(e) => setCheckoutForm({ ...checkoutForm, bik: e.target.value })}
                            placeholder="044525225"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="checkoutBankName">Название банка</Label>
                          <Input
                            id="checkoutBankName"
                            value={checkoutForm.bank_name}
                            onChange={(e) => setCheckoutForm({ ...checkoutForm, bank_name: e.target.value })}
                            placeholder="ПАО «Банк»"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Текст об оплате */}
                    <div className="bg-muted/50 border border-border rounded-lg p-4">
                      <p className="text-sm text-muted-foreground">
                        <strong>Оплата только с расчетного счета компании или ИП</strong>
                      </p>
                    </div>

                    <PersonalDataConsent
                      id="dashboard-checkout-consent"
                      className="pt-2"
                      checked={checkoutConsent}
                      onCheckedChange={(v) => setCheckoutConsent(Boolean(v))}
                    />

                    <MarketingConsent
                      id="dashboard-checkout-marketing-consent"
                      className="pt-2"
                      checked={checkoutMarketingConsent}
                      onCheckedChange={(v) => setCheckoutMarketingConsent(Boolean(v))}
                    />

                    {/* Кнопки */}
                    <div className="flex justify-between pt-4">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCheckoutStep(1)}
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Назад
                      </Button>
                      <Button
                        type="submit"
                        disabled={checkoutLoading}
                        className="bg-accent hover:bg-accent/90"
                      >
                        {checkoutLoading ? 'Оформление...' : 'Оформить заказ'}
                        {!checkoutLoading && <Check className="w-4 h-4 ml-2" />}
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-12">
                    {/* Иконка успеха - улыбающаяся девушка */}
                    <div className="mb-6 flex justify-center">
                      <div className="w-32 h-32 rounded-full bg-accent/10 flex items-center justify-center">
                        <span className="text-6xl">👩‍💼</span>
                      </div>
                    </div>
                    
                    <h2 className="text-3xl font-bold mb-4">Заказ успешно оформлен!</h2>
                    
                    {orderNumber && (
                      <div className="mb-6">
                        <p className="text-lg text-muted-foreground mb-2">Номер вашего заказа:</p>
                        <p className="text-4xl font-bold text-accent">{orderNumber}</p>
                      </div>
                    )}
                    
                    <div className="max-w-md mx-auto space-y-4 text-left bg-muted/50 rounded-lg p-6 mb-6">
                      <p className="text-sm text-muted-foreground">
                        Ваш заказ принят в обработку. В ближайшее время с вами свяжется наш менеджер для подтверждения деталей заказа.
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Информация о заказе и инструкции по доступу будут отправлены на указанный email: <strong>{checkoutForm.email}</strong>
                      </p>
                    </div>
                    
                    <div className="flex gap-4 justify-center">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setCheckoutStep(1);
                          setOrderNumber(null);
                        }}
                      >
                        Вернуться в корзину
                      </Button>
                      <Button
                        onClick={async () => {
                          await loadPurchases();
                          setActiveTab('purchases');
                          setCheckoutStep(1);
                          setOrderNumber(null);
                        }}
                        className="bg-accent hover:bg-accent/90"
                      >
                        Перейти к покупкам
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="purchases" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Покупки</CardTitle>
                <CardDescription>
                  История ваших покупок
                </CardDescription>
              </CardHeader>
              <CardContent>
                {purchasesLoading ? (
                  <div className="text-center py-8">Загрузка...</div>
                ) : purchases.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    У вас пока нет покупок
                  </div>
                ) : (
                  <div className="space-y-4">
                    {purchases.map((purchase) => (
                      <Card key={purchase.id}>
                        <CardContent className="pt-6">
                          <div className="flex justify-between items-start">
                            <div className="flex-1">
                              <h3 className="font-semibold text-lg">{purchase.product?.name}</h3>
                              
                              {/* Номер заказа */}
                              {purchase.order_number && (
                                <p className="text-sm font-medium text-accent mt-1">
                                  Номер заказа: {purchase.order_number}
                                </p>
                              )}
                              
                              <p className="text-sm text-muted-foreground mt-1">
                                Дата покупки: {formatDate(purchase.purchase_date)}
                              </p>
                              
                              {/* Информация о заказе */}
                              {purchase.order_data && (
                                <div className="mt-4 space-y-3 border-t border-border/50 pt-4">
                                  {/* Контактная информация */}
                                  {(purchase.order_data.contact_name || purchase.order_data.email || purchase.order_data.contact_phone) && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">Контактная информация:</p>
                                      <div className="space-y-1 text-sm text-muted-foreground">
                                        {purchase.order_data.contact_name && (
                                          <p>Контактное лицо: <span className="text-foreground">{purchase.order_data.contact_name}</span></p>
                                        )}
                                        {purchase.order_data.email && (
                                          <p>Email: <span className="text-foreground">{purchase.order_data.email}</span></p>
                                        )}
                                        {purchase.order_data.contact_phone && (
                                          <p>Телефон: <span className="text-foreground">{purchase.order_data.contact_phone}</span></p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Информация о компании */}
                                  {(purchase.order_data.organization_name || purchase.order_data.inn) && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">Информация о компании:</p>
                                      <div className="space-y-1 text-sm text-muted-foreground">
                                        {purchase.order_data.organization_name && (
                                          <p>Организация: <span className="text-foreground">{purchase.order_data.organization_name}</span></p>
                                        )}
                                        {purchase.order_data.inn && (
                                          <p>ИНН: <span className="text-foreground">{purchase.order_data.inn}</span></p>
                                        )}
                                        {purchase.order_data.legal_address && (
                                          <p>Юридический адрес: <span className="text-foreground">{purchase.order_data.legal_address}</span></p>
                                        )}
                                        {purchase.order_data.correspondence_address && (
                                          <p>Адрес для корреспонденции: <span className="text-foreground">{purchase.order_data.correspondence_address}</span></p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Реквизиты банка */}
                                  {(purchase.order_data.bank_name || purchase.order_data.settlement_account || purchase.order_data.bik) && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">Реквизиты банка:</p>
                                      <div className="space-y-1 text-sm text-muted-foreground">
                                        {purchase.order_data.bank_name && (
                                          <p>Банк: <span className="text-foreground">{purchase.order_data.bank_name}</span></p>
                                        )}
                                        {purchase.order_data.settlement_account && (
                                          <p>Расчетный счет: <span className="text-foreground">{purchase.order_data.settlement_account}</span></p>
                                        )}
                                        {purchase.order_data.correspondent_account && (
                                          <p>Корреспондентский счет: <span className="text-foreground">{purchase.order_data.correspondent_account}</span></p>
                                        )}
                                        {purchase.order_data.bik && (
                                          <p>БИК: <span className="text-foreground">{purchase.order_data.bik}</span></p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Информация об услугах из калькулятора */}
                                  {purchase.order_data.calculator_data && (
                                    <div>
                                      <p className="text-sm font-medium mb-2">Услуги:</p>
                                      <div className="space-y-1 text-sm text-muted-foreground">
                                        {purchase.order_data.calculator_data.userCount && (
                                          <p>Количество пользователей: <span className="text-foreground">{purchase.order_data.calculator_data.userCount}</span></p>
                                        )}
                                        {purchase.order_data.calculator_data.selectedServices && 
                                         purchase.order_data.calculator_data.selectedServices.length > 0 && (
                                          <div>
                                            <p className="mb-1">Дополнительные услуги:</p>
                                            <ul className="list-disc list-inside ml-2">
                                              {purchase.order_data.calculator_data.selectedServices.map((service, idx) => (
                                                <li key={idx}>
                                                  {service.name}
                                                  <span className="ml-2 font-medium text-foreground">
                                                    ({service.price
                                                      ? formatPrice(service.price)
                                                      : (service.priceDisplay || 'по запросу')})
                                                  </span>
                                                </li>
                                              ))}
                                            </ul>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                              
                              <div className="mt-3 pt-2 border-t border-border/30 space-y-1">
                                {purchase.monthly_price && (
                                  <p className="text-sm">
                                    Стоимость в месяц: <span className="font-medium">{formatPrice(purchase.monthly_price)}</span>
                                  </p>
                                )}
                                {purchase.one_time_price && (
                                  <p className="text-sm">
                                    Разовая стоимость: <span className="font-medium">{formatPrice(purchase.one_time_price)}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="management" className="mt-6">
            <CabinetSubscriptions />
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
};

export default DashboardPage;
